import { Link, useNavigate } from "react-router-dom";
import { ChevronDown, Menu, Search, X } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { useState, useEffect, useRef, lazy, Suspense } from "react";
import { getAuthService } from "../lib/firebaseClient";

const dropdowns = {
  "s-orienter": {
    label: "S'orienter",
    width: "w-64",
    links: [
      { to: "/s-orienter/debut", label: "Par où commencer ?", desc: "Comprendre le parcours d'entrée" },
      { to: "/s-orienter/etudes", label: "Quelles études ?", desc: "Écoles, masters et trajectoires" },
      { to: "/s-orienter/metiers", label: "Métiers en bref", desc: "Sales, trading, M&A, risk" },
    ],
  },
  stages: {
    label: "Stages",
    width: "w-64",
    links: [
      { to: "/stages/calendrier", label: "Calendrier & types de stage", desc: "Timing, césure et off-cycle" },
      { to: "/stages/pre-master", label: "Pré-Master", desc: "Premier stage utile" },
      { to: "/stages/cesure", label: "Césure", desc: "Construire une candidature forte" },
    ],
  },
  candidatures: {
    label: "Candidatures",
    width: "w-72",
    links: [
      { to: "/candidature/cv-en-finance", label: "CV & lettres", desc: "Documents clairs et crédibles" },
      { to: "/candidature/ou-postuler-en-finance", label: "Où postuler ?", desc: "Banques, fonds et plateformes" },
      { to: "/candidature/networking", label: "Networking", desc: "Contacter sans paraître amateur" },
      { to: "/candidature/process", label: "Process", desc: "Tests, entretiens et étapes" },
    ],
  },
};

const directLinks = [
  { to: "/entretiens", label: "Entretiens" },
  { to: "/cours", label: "Cours" },
  { href: "https://road-to-la-defense.fr/forum", label: "Forum" },
  { to: "/glossaire", label: "Glossaire" },
  { to: "/blog", label: "Blog" },
  { to: "/contact", label: "Contact" },
];

const navLinkClass =
  "rounded-full px-2.5 py-2 font-medium text-white/82 transition hover:bg-white/10 hover:text-white";
const menuButtonClass =
  "inline-flex items-center gap-1 rounded-full px-2.5 py-2 font-medium text-white/82 transition hover:bg-white/10 hover:text-white";
const mobileRowClass =
  "block rounded-lg px-3 py-2.5 text-sm font-medium text-white/88 transition hover:bg-white/10 hover:text-white";

export default function Navbar() {
  const [openDropdown, setOpenDropdown] = useState(null);
  const [isAuthModalOpen, setIsAuthModalOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [openMobileSubMenu, setOpenMobileSubMenu] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const { currentUser } = useAuth();
  const navigate = useNavigate();

  const dropdownRefs = useRef({});
  const AuthModal = lazy(() => import("./AuthModal"));

  const closeMenus = () => {
    setOpenDropdown(null);
    setIsMobileMenuOpen(false);
    setOpenMobileSubMenu(null);
  };

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen((previous) => !previous);
    setOpenDropdown(null);
  };

  const handleSearchSubmit = (event) => {
    event.preventDefault();
    const trimmedQuery = searchQuery.trim();

    if (!trimmedQuery) return;

    closeMenus();
    navigate(`/recherche?q=${encodeURIComponent(trimmedQuery)}`);
  };

  const handleSignOut = async () => {
    const [{ signOut }, auth] = await Promise.all([
      import("firebase/auth").then((module) => ({ signOut: module.signOut })),
      getAuthService(),
    ]);
    await signOut(auth);
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      const clickedInside = Object.values(dropdownRefs.current).some((node) =>
        node?.contains(event.target)
      );

      if (!clickedInside) {
        setOpenDropdown(null);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <>
      <nav className="sticky top-0 z-50 border-b border-white/10 bg-[#123f43] text-sm text-white shadow-[0_10px_30px_rgba(18,63,67,0.18)]">
        <div className="mx-auto flex min-h-[64px] max-w-[1500px] items-center justify-between gap-5 px-4 sm:px-6 lg:px-8">
          <div className="flex min-w-0 flex-1 items-center gap-6">
            <Link to="/" className="group flex shrink-0 items-center gap-2.5" onClick={closeMenus}>
              <img
                src="/img/logo.webp"
                alt=""
                className="h-10 w-10 object-contain brightness-0 invert"
                aria-hidden="true"
              />
              <span className="hidden max-w-[9.5rem] text-[15px] font-semibold leading-tight tracking-wide text-white sm:block">
                Road to La Défense
              </span>
            </Link>

            <div className="hidden min-w-0 flex-1 items-center justify-center gap-1 lg:flex">
              {Object.entries(dropdowns).map(([key, dropdown]) => (
                <div
                  key={key}
                  className="relative"
                  ref={(node) => {
                    dropdownRefs.current[key] = node;
                  }}
                >
                  <button
                    type="button"
                    onClick={() => setOpenDropdown(openDropdown === key ? null : key)}
                    className={menuButtonClass}
                    aria-expanded={openDropdown === key}
                  >
                    {dropdown.label}
                    <ChevronDown
                      className={`h-4 w-4 transition ${openDropdown === key ? "rotate-180" : ""}`}
                    />
                  </button>

                  {openDropdown === key && (
                    <div
                      className={`absolute left-0 top-full mt-3 ${dropdown.width} overflow-hidden rounded-xl border border-[#c8e6e8] bg-white p-2 text-gray-900 shadow-xl shadow-[#123f43]/20`}
                    >
                      {dropdown.links.map((link) => (
                        <Link
                          key={link.to}
                          to={link.to}
                          className="block rounded-lg px-3 py-2.5 transition hover:bg-[#f4fafb]"
                          onClick={() => setOpenDropdown(null)}
                        >
                          <span className="block text-sm font-semibold text-[#18565b]">
                            {link.label}
                          </span>
                          <span className="mt-0.5 block text-xs leading-5 text-gray-500">
                            {link.desc}
                          </span>
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              ))}

              {directLinks.map((link) =>
                link.to ? (
                  <Link key={link.to} to={link.to} className={navLinkClass}>
                    {link.label}
                  </Link>
                ) : (
                  <a key={link.href} href={link.href} className={navLinkClass}>
                    {link.label}
                  </a>
                )
              )}
            </div>
          </div>

          <div className="flex shrink-0 items-center gap-3">
            <form onSubmit={handleSearchSubmit} className="relative hidden items-center md:flex">
              <label htmlFor="site-search-nav" className="sr-only">
                Rechercher sur le site
              </label>
              <Search className="pointer-events-none absolute left-3 h-4 w-4 text-[#9ed6da]" />
              <input
                id="site-search-nav"
                type="search"
                value={searchQuery}
                onChange={(event) => setSearchQuery(event.target.value)}
                placeholder="Rechercher..."
                className="w-40 rounded-full border border-white/15 bg-white/10 py-2 pl-9 pr-4 text-sm text-white placeholder:text-white/55 outline-none transition focus:border-[#2e9ca3] focus:bg-white focus:text-gray-900 focus:placeholder:text-gray-400 2xl:w-56"
              />
            </form>

            <div className="hidden items-center gap-2 md:flex">
              {currentUser ? (
                <>
                  <Link to="/profil" className={navLinkClass}>
                    Profil
                  </Link>
                  <button
                    type="button"
                    onClick={handleSignOut}
                    className="rounded-full border border-white/15 px-3 py-2 font-medium text-white/82 transition hover:bg-white/10 hover:text-white"
                  >
                    Déconnexion
                  </button>
                </>
              ) : (
                <button
                  type="button"
                  onClick={() => setIsAuthModalOpen(true)}
                  className="rounded-full bg-white px-4 py-2 font-semibold text-[#123f43] shadow-sm transition hover:bg-[#e6f1f2]"
                >
                  Connexion
                </button>
              )}
            </div>

            <button
              type="button"
              onClick={toggleMobileMenu}
              className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-white/15 bg-white/10 text-white transition hover:bg-white/15 lg:hidden"
              aria-label={isMobileMenuOpen ? "Fermer le menu de navigation" : "Ouvrir le menu de navigation"}
              aria-expanded={isMobileMenuOpen}
            >
              {isMobileMenuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </button>
          </div>
        </div>

        {isMobileMenuOpen && (
          <div className="border-t border-white/10 bg-[#123f43] px-4 pb-5 pt-3 shadow-xl lg:hidden">
            <div className="mx-auto max-w-7xl space-y-3">
              <form onSubmit={handleSearchSubmit} className="relative">
                <label htmlFor="site-search-mobile" className="sr-only">
                  Rechercher sur le site
                </label>
                <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[#9ed6da]" />
                <input
                  id="site-search-mobile"
                  type="search"
                  value={searchQuery}
                  onChange={(event) => setSearchQuery(event.target.value)}
                  placeholder="Rechercher sur le site..."
                  className="w-full rounded-xl border border-white/15 bg-white/10 py-3 pl-9 pr-4 text-sm text-white placeholder:text-white/55 outline-none focus:border-[#2e9ca3]"
                />
              </form>

              <div className="space-y-1">
                {Object.entries(dropdowns).map(([key, dropdown]) => (
                  <div key={key}>
                    <button
                      type="button"
                      className="flex w-full items-center justify-between rounded-lg px-3 py-2.5 text-left text-sm font-semibold text-white/90 transition hover:bg-white/10 hover:text-white"
                      onClick={() => setOpenMobileSubMenu(openMobileSubMenu === key ? null : key)}
                    >
                      {dropdown.label}
                      <ChevronDown
                        className={`h-4 w-4 transition ${openMobileSubMenu === key ? "rotate-180" : ""}`}
                      />
                    </button>
                    {openMobileSubMenu === key && (
                      <div className="ml-3 mt-1 space-y-1 border-l border-white/15 pl-3">
                        {dropdown.links.map((link) => (
                          <Link
                            key={link.to}
                            to={link.to}
                            className={mobileRowClass}
                            onClick={closeMenus}
                          >
                            {link.label}
                          </Link>
                        ))}
                      </div>
                    )}
                  </div>
                ))}

                {directLinks.map((link) =>
                  link.to ? (
                    <Link key={link.to} to={link.to} className={mobileRowClass} onClick={closeMenus}>
                      {link.label}
                    </Link>
                  ) : (
                    <a key={link.href} href={link.href} className={mobileRowClass} onClick={closeMenus}>
                      {link.label}
                    </a>
                  )
                )}
              </div>

              <div className="border-t border-white/10 pt-3">
                {currentUser ? (
                  <div className="space-y-1">
                    <Link to="/profil" className={mobileRowClass} onClick={closeMenus}>
                      Profil
                    </Link>
                    <button
                      type="button"
                      onClick={() => {
                        handleSignOut();
                        closeMenus();
                      }}
                      className="block w-full rounded-lg px-3 py-2.5 text-left text-sm font-medium text-white/88 transition hover:bg-white/10 hover:text-white"
                    >
                      Déconnexion
                    </button>
                  </div>
                ) : (
                  <button
                    type="button"
                    onClick={() => {
                      setIsAuthModalOpen(true);
                      closeMenus();
                    }}
                    className="w-full rounded-xl bg-white px-4 py-3 text-sm font-semibold text-[#123f43] transition hover:bg-[#e6f1f2]"
                  >
                    Connexion / inscription
                  </button>
                )}
              </div>
            </div>
          </div>
        )}
      </nav>

      {isAuthModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4">
          <div className="w-full max-w-md rounded-xl bg-white p-6 shadow-xl">
            <Suspense fallback={<div>Chargement...</div>}>
              <AuthModal close={() => setIsAuthModalOpen(false)} />
            </Suspense>
          </div>
        </div>
      )}
    </>
  );
}
